'use client';

import { useEffect, useRef, useState } from 'react';
import { FaBell, FaCheckCircle } from 'react-icons/fa';

interface WaitlistFormProps {
  /** Subscription source tag — lets you see which page drove each signup.
   *  Defaults to the main Obscura Pro page. */
  source?: string;
}

export default function WaitlistForm({ source = 'smart-shield-waitlist' }: WaitlistFormProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [error, setError] = useState('');
  // Honeypot — hidden from humans; rejected server-side when filled.
  const [honeypot, setHoneypot] = useState('');
  // Time-to-submit: when the form became usable. Sent as elapsed ms (client
  // clock only) so the server can reject instant, scripted submissions.
  const formStartRef = useRef(0);
  useEffect(() => { formStartRef.current = Date.now(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setError('');

    try {
      const res = await fetch('/api/email-subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source, company_website: honeypot, formElapsedMs: formStartRef.current ? Date.now() - formStartRef.current : undefined }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to subscribe');
      setStatus('success');
      setEmail('');
    } catch (err: any) {
      setStatus('error');
      setError(err.message || 'Something went wrong');
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 max-w-md mx-auto text-center">
        <FaCheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
        <h3 className="text-xl font-bold text-green-900 mb-2">You're on the list!</h3>
        <p className="text-green-700 text-sm">
          We'll email you the moment Obscura Pro™ is available. You'll be among the first to know.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-md mx-auto">
      {/* Honeypot — invisible to humans, irresistible to form-filling bots. */}
      <div aria-hidden="true" className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden">
        <label htmlFor="company_website_waitlist">Company website (leave blank)</label>
        <input maxLength={80}
          id="company_website_waitlist"
          name="company_website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>
      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-200">
        <div className="flex items-center gap-2 mb-3">
          <FaBell className="w-5 h-5 text-[var(--ob-bronze-text,var(--brand-link,var(--primary-red)))]" />
          <h3 className="text-lg font-bold text-gray-900">Join the Waitlist</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Be the first to know when Obscura Pro™ launches. Early waitlist members get exclusive pricing and priority shipping.
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input maxLength={254}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="flex-1 px-4 py-3 border border-[var(--ob-line,#d1d5db)] rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--ob-bronze-text,var(--primary-red))] focus:border-transparent"
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className="bg-[var(--ob-bronze,var(--primary-red))] hover:bg-[var(--ob-champagne,var(--primary-dark))] text-[var(--ob-ink,#fff)] font-bold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ob-bronze-text,var(--primary-red))] focus-visible:ring-offset-2"
          >
            {status === 'loading' ? 'Joining...' : 'Notify Me'}
          </button>
        </div>
        {error && <p className="text-red-600 text-xs mt-2">{error}</p>}
        <p className="text-[11px] text-gray-400 mt-3">
          No spam. We'll only email you when there's news about Obscura Pro™.
        </p>
      </div>
    </form>
  );
}
